import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { BehaviorSubject, catchError, config, connect, Observable, tap, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType, HttpParams, HttpResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { ReponseFiltre } from './reponses/reponse-filtre';
import { ReponseBase } from './reponse-base';
@Injectable({ providedIn: 'root' })
export class BaseService {

    constructor(
        public messageService: MessageService,
        public router: Router
    ) { }

    public GenerateFilterParams(event: LazyLoadEvent): HttpParams {
        let params = new HttpParams();

        if (event.first) {
            params = params.set('first', event.first.toString());
        }

        if (event.rows) {
            params = params.set('rows', event.rows.toString());
        }

        if (event.sortField && event.sortOrder) {
            params = params.set('sortField', event.sortField);
            params = params.set('sortOrder', event.sortOrder.toString());
        }

        if (event.globalFilter) {
            params = params.set('globalFilter', event.globalFilter.toString());
        }

        if (event.multiSortMeta) {
            params = params.set('multiSortMeta', JSON.stringify(event.multiSortMeta));
        }

        if (event.filters) {
            var filtres = JSON.parse(JSON.stringify(event.filters));
            delete filtres.global;
            Object.keys(filtres).forEach(key => {
                if (!Array.isArray(filtres[key])) {
                    filtres[key] = [filtres[key]];
                }
                if (Array.isArray(filtres[key]) && filtres[key].length == 1 && filtres[key][0].value == null) {
                    delete filtres[key];
                }
            });
            params = params.set('filters', JSON.stringify(filtres));
        }

        return params;
    }

    protected handleError(message: any): Observable<any> {
        if (typeof message.error === 'string') {
            this.messageService.add({ severity: 'error', summary: 'Une erreur est survenue', detail: message.error, life: 10000 });
        } else if (Array.isArray(message.error)) {
            message.error.forEach((m, i) => {
                setTimeout(() => {
                    this.messageService.add({ severity: m.severity, summary: m.code, detail: m.description, life: 10000 });
                }, 1000 * i);
            });
        } else if (message.error instanceof ProgressEvent) {
            // Handle ProgressEvent errors (network issues, timeouts, etc.)
            this.messageService.add({ 
                severity: 'error', 
                summary: 'Erreur de connexion', 
                detail: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet et réessayez.', 
                life: 10000 
            });
        } else if (message.error.messages) {
            message.error.messages.forEach((m, i) => {
                setTimeout(() => {
                    this.messageService.add({ severity: m.severity, summary: m.code, detail: m.description, life: 10000 });
                }, 1000 * i);
            });
        } else if (typeof message.error === 'object') {
            // Handle .NET validation errors
            if (message.error.errors && typeof message.error.errors === 'object') {
                // This is a .NET validation error response
                const errors = message.error.errors;
                let errorIndex = 0;
                
                Object.keys(errors).forEach(fieldKey => {
                    const fieldErrors = errors[fieldKey];
                    if (Array.isArray(fieldErrors)) {
                        fieldErrors.forEach((errorMessage: string) => {
                            setTimeout(() => {
                                // Make field names more readable by replacing array indices
                                const readableFieldKey = fieldKey.replace(/\[(\d+)\]/g, (match, index) => {
                                    return ` (${parseInt(index) + 1})`;
                                });
                                
                                this.messageService.add({ 
                                    severity: 'error', 
                                    summary: 'Erreur de validation', 
                                    detail: `${readableFieldKey}: ${errorMessage}`, 
                                    life: 10000 
                                });
                            }, 1000 * errorIndex);
                            errorIndex++;
                        });
                    }
                });
            } else {
                // Handle other object errors
                this.messageService.add({ 
                    severity: message.error.severity || 'error', 
                    summary: message.error.code || 'Erreur', 
                    detail: message.error.description || 'Une erreur est survenue', 
                    life: 10000 
                });
            }
        }
        return throwError(() => new Error(message));
    }

    public responseHandler = (response: HttpResponse<any>): Observable<HttpResponse<any>> => {
        switch (response.status) {
            case 201:
                this.messageService.add({ severity: 'success', summary: 'Succès', detail: 'Création effectuée avec succès' });
                return;
            case 202:
                this.messageService.add({ severity: 'info', summary: 'Info', detail: 'Opération en cours' });
                return;
            case 204:
                this.messageService.add({ severity: 'info', summary: 'Info', detail: 'Aucun contenu' });
                return;
            default:
                return;
        }
    }

    public errorHandler = (response: HttpResponse<any>, objet: string): Observable<HttpErrorResponse> => {
        switch (response.status) {
            case 400:
                this.messageService.add({ severity: 'error', summary: 'Erreur', detail: `Erreur de requête pour ${objet}` });
                break;
            case 401:
                this.messageService.add({ severity: 'error', summary: 'Erreur', detail: `Accès non autorisé pour ${objet}` });
                break;
            case 403:
                this.messageService.add({ severity: 'error', summary: 'Erreur', detail: `Accès non autorisé pour ${objet}` });
                break;
            case 404:
                this.messageService.add({ severity: 'error', summary: 'Erreur', detail: `Ressource non trouvée pour ${objet}` });
                break;
            case 405:
                this.messageService.add({ severity: 'error', summary: 'Erreur', detail: `Méthode non autorisée pour ${objet}` });
                break;
            case 409:
                this.messageService.add({ severity: 'error', summary: 'Erreur', detail: `La ressource existe déjà pour ${objet}` });
                break;
            case 500:
                this.messageService.add({ severity: 'error', summary: 'Erreur', detail: `Erreur interne du serveur pour ${objet}: ${response.body}` });
                break;
            default:
                this.router.navigate(['/error']);
        }
        return;
    }
}

export class CacheMap<K, V> {
    private cache: Map<K, V> = new Map();
    private cacheKeys: K[] = [];
    private readonly MAX_CACHE_SIZE: number;

    constructor(maxCacheSize: number = 30) {
        this.MAX_CACHE_SIZE = maxCacheSize;
    }

    public set(key: K, value: V): void {
        if (this.cache.has(key)) {
            const index = this.cacheKeys.indexOf(key);
            if (index !== -1) {
                this.cacheKeys.splice(index, 1);
            }
        }

        this.cacheKeys.push(key);

        this.cache.set(key, value);

        if (this.cacheKeys.length > this.MAX_CACHE_SIZE) {
            const oldestKey = this.cacheKeys.shift();
            if (oldestKey !== undefined) {
                this.cache.delete(oldestKey);
            }
        }
    }

    public get(key: K): V | undefined {
        return this.cache.get(key);
    }

    public has(key: K): boolean {
        return this.cache.has(key);
    }

    public delete(key: K): boolean {
        const index = this.cacheKeys.indexOf(key);
        if (index !== -1) {
            this.cacheKeys.splice(index, 1);
        }
        return this.cache.delete(key);
    }

    public clear(): void {
        this.cache.clear();
        this.cacheKeys = [];
    }

    public size(): number {
        return this.cache.size;
    }

    public forEach(callbackfn: (value: V, key: K, map: Map<K, V>) => void, thisArg?: any): void {
        this.cache.forEach(callbackfn, thisArg);
    }
}

export interface HubWatcherConfig {
    hubConnection?: HubConnection;
    idField?: string;
    /** Noms d'événements SignalR (défaut: `Add`/`Update`/`Delete` ; hub unifié: `canal:Add/...`). */
    events?: {
        add?: string;
        update?: string;
        delete?: string;
    };
}

function hubEventNames(hub?: HubWatcherConfig): { add: string; update: string; delete: string } {
    return {
        add: hub?.events?.add ?? 'Add',
        update: hub?.events?.update ?? 'Update',
        delete: hub?.events?.delete ?? 'Delete'
    };
}

export class Store<T> {
    private onUpdateSignal = new BehaviorSubject<{ type: 'add' | 'update' | 'delete', data: any }>(null);
    public response = signal<ReponseFiltre<T>>(null);
    public error = signal<any>(null);
    public state = signal<HttpEventType>(null);
    public onUpdate: Observable<{ type: 'add' | 'update' | 'delete', data: any }> = this.onUpdateSignal.asObservable();

    constructor(private httpEvents: Observable<HttpEvent<T>>) {
        this.httpEvents.pipe(
            tap((event: HttpEvent<T>) => {
                this.state.set(event.type);

                if (event.type === HttpEventType.Response && event.body) {
                    this.response.set(event.body as ReponseFiltre<T>);
                    this.handleResponse();
                }
            }),
            catchError(err => {
                this.error.set(err);
                return throwError(() => err);
            })
        ).subscribe();
    }

    protected handleResponse(): void {
    }
}

export class ArrayStore<T> {
    private paramsCache: CacheMap<string, ReponseFiltre<T>> = new CacheMap<string, ReponseFiltre<T>>();
    private currentParams: any = {};
    private httpClient: HttpClient;
    private apiUrl: string;
    private initialRequestMade = false;
    private reconnectAttempts = 0;
    public elements = signal<T[]>([]);
    public maxCount = signal<number>(0);
    private onUpdateSignal = new BehaviorSubject<{ type: 'add' | 'update' | 'delete', data: any }>(null);
    public response = signal<ReponseFiltre<T>>(null);
    public error = signal<any>(null);
    public state = signal<HttpEventType>(null);
    public onUpdate: Observable<{ type: 'add' | 'update' | 'delete', data: any }> = this.onUpdateSignal.asObservable();

    constructor(
        private httpEvents: Observable<HttpEvent<ReponseFiltre<T>>>,
        private hub?: HubWatcherConfig,
        httpClient?: HttpClient,
        apiUrl?: string
    ) {
        this.httpClient = httpClient;
        this.apiUrl = apiUrl;
    }

    protected handleResponse(): void {
        // Cache the response with the current parameters
        const paramsKey = this.generateParamsKey(this.currentParams);
        this.paramsCache.set(paramsKey, this.response());

        if (this.hub?.hubConnection) {
            this.connect();
        }
    }

    public getWithParams(event: LazyLoadEvent, additionalParams: any): ReponseFiltre<T> {
        // Create a unique key from the parameters
        const params = [JSON.parse(JSON.stringify(event)), additionalParams];
        delete params[0].filters.global;
        params[0].filters = Object.fromEntries(
            Object.entries(params[0].filters).filter(([_, value]) => value !== null)
        );
        const paramsKey = this.generateParamsKey(params);

        // If we have a cached response for these parameters, use it
        if (this.paramsCache.has(paramsKey)) {
            this.currentParams = params;
            // Return the cached response without updating any signals
            this.response.set(this.paramsCache.get(paramsKey));
            this.elements.set(this.response().elements);
            this.maxCount.set(this.response().maxCount);
            this.error.set(null);
            this.state.set(HttpEventType.Response);

            this.prefetchAdjacentPages(paramsKey, additionalParams);
            return this.paramsCache.get(paramsKey);
        }

        // If not cached, fetch new data with these parameters
        this.currentParams = params;
        this.fetchData(event, additionalParams);

        // Return current response (may be outdated until the new data arrives)
        return this.response() || { elements: [], maxCount: 0 } as ReponseFiltre<T>;
    }

    private fetchData(event: LazyLoadEvent, additionalParams: any): void {
        if (!this.httpClient || !this.apiUrl) {
            console.error('HTTP client or API URL not provided to ArrayStore');
            return;
        }

        // Convert LazyLoadEvent to HttpParams
        let httpParams = new HttpParams();
        httpParams = httpParams.set('first', event.first?.toString() || '0');
        httpParams = httpParams.set('rows', event.rows?.toString() || '10');

        if (event.sortField && event.sortOrder) {
            httpParams = httpParams.set('sortField', event.sortField);
            httpParams = httpParams.set('sortOrder', event.sortOrder.toString());
        }

        if (event.globalFilter) {
            httpParams = httpParams.set('globalFilter', event.globalFilter.toString());
        }

        if (event.multiSortMeta) {
            httpParams = httpParams.set('multiSortMeta', JSON.stringify(event.multiSortMeta));
        }

        if (event.filters) {
            const filters = JSON.parse(JSON.stringify(event.filters));
            delete filters.global;
            Object.keys(filters).forEach(key => {
                if (!Array.isArray(filters[key])) {
                    filters[key] = [filters[key]];
                }
                if (Array.isArray(filters[key]) && filters[key].length == 1 && filters[key][0].value == null) {
                    delete filters[key];
                }
            });
            httpParams = httpParams.set('filters', JSON.stringify(filters));
        }

        if (typeof additionalParams === 'object' && additionalParams !== null) {
            Object.keys(additionalParams).forEach(key => {
                const value = additionalParams[key];
                if (value !== undefined && value !== null) {
                    httpParams = httpParams.set(key, String(value));
                }
            });
        }

        let params = [JSON.parse(JSON.stringify(event)), additionalParams];
        delete params[0].filters.global;
        params[0].filters = Object.fromEntries(
            Object.entries(params[0].filters).filter(([_, value]) => value !== null)
        );

        // Make the HTTP request
        this.httpClient.get<ReponseFiltre<T>>(this.apiUrl, {
            params: httpParams,
            observe: 'events'
        }).pipe(
            tap((event: HttpEvent<ReponseFiltre<T>>) => {
                const latestParamsKey = this.generateParamsKey(params);
                const currentParamsKey = this.generateParamsKey(this.currentParams);

                if (currentParamsKey === latestParamsKey) {
                    this.state.set(event.type);
                } 
                    
                if (event.type === HttpEventType.Response && event.body) {
                    if (currentParamsKey === latestParamsKey) { 
                        this.response.set(event.body);
                        this.elements.set(event.body.elements);
                        this.maxCount.set(event.body.maxCount);
                        this.handleResponse();
            
                        // Prefetch next and previous pages
                        this.prefetchAdjacentPages(currentParamsKey, additionalParams);
                    } 
                        
                    this.paramsCache.set(latestParamsKey, event.body);
                }
            }),
            catchError(err => {
                this.error.set(err);
                return throwError(() => err);
            })
        ).subscribe();
    }

    private generateParamsKey(params: any[]): string {
        // Create a string key from the parameters
        // For LazyLoadEvent, extract the relevant parts
        if (params.length > 0 && params[0] && typeof params[0] === 'object') {
            const event = params[0] as LazyLoadEvent;
            const eventKey = JSON.stringify({
                first: event.first,
                rows: event.rows,
                sortField: event.sortField,
                sortOrder: event.sortOrder,
                globalFilter: event.globalFilter,
                multiSortMeta: event.multiSortMeta,
                filters: event.filters
            });

            // Combine with the rest of the params
            return eventKey + JSON.stringify(params.slice(1));
        }

        return JSON.stringify(params);
    }

    private connect(): void {
        this.startConnection();
        this.registerOnServerEvents();
    }

    private startConnection(): void {
        if (!this.hub?.hubConnection) return;

        const state = this.hub.hubConnection.state;
        if (state === HubConnectionState.Connected || state === HubConnectionState.Connecting || state === HubConnectionState.Reconnecting) {
            return;
        }

        this.hub.hubConnection
            .start()
            .then(() => {
                console.log('Connection to hub established');
                this.reconnectAttempts = 0;
            })
            .catch(err => {
                this.reconnectAttempts++;
                const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000);
                console.error(`Error while establishing connection to hub (attempt ${this.reconnectAttempts}):`, err);
                setTimeout(() => this.startConnection(), delay);
            });
    }

    private registerOnServerEvents(): void {
        const names = hubEventNames(this.hub);
        this.hub?.hubConnection.on(names.update, (data: T) => {
            if (this.response()) {
                const updatedElements = this.response().elements.map(x =>
                    x[this.hub?.idField] === data[this.hub?.idField] ? data : x
                );

                this.response.set({
                    ...this.response(),
                    elements: updatedElements
                });

                this.onUpdateSignal.next({ type: 'update', data: data });
            }
        });

        this.hub?.hubConnection.on(names.add, (data: T) => {
            if (this.response()) {
                const newElements = [...this.response().elements, data];

                this.response.set({
                    ...this.response(),
                    elements: newElements,
                    maxCount: this.response().maxCount + 1
                });

                this.onUpdateSignal.next({ type: 'add', data: data });
            }
        });

        this.hub?.hubConnection.on(names.delete, (id: number) => {
            if (this.response()) {
                const itemToDelete = this.response().elements.find(x => x[this.hub?.idField] === id);

                if (itemToDelete) {
                    this.onUpdateSignal.next({ type: 'delete', data: itemToDelete });

                    const filteredElements = this.response().elements.filter(x => x[this.hub?.idField] !== id);

                    this.response.set({
                        ...this.response(),
                        elements: filteredElements,
                        maxCount: this.response().maxCount - 1
                    });
                }
            }
        });
    }

    /**
     * Prefetches adjacent pages (next and previous) without updating signals
     */
    private prefetchAdjacentPages(currentParamsKey: string, additionalParams: any[]): void {
        if (!this.httpClient || !this.apiUrl || !this.response()) {
            return;
        }
        
        const currentEvent = this.currentParams[0] as LazyLoadEvent;
        const currentFirst = currentEvent.first || 0;
        const rows = currentEvent.rows || 10;
        const totalRows = this.response().maxCount;
        
        // Only prefetch next page if there is one
        if (currentFirst + rows < totalRows) {
            this.prefetchPage(currentFirst + rows, rows, currentEvent, additionalParams);
        }
        
        // Only prefetch previous page if there is one
        if (currentFirst - rows >= 0) {
            this.prefetchPage(currentFirst - rows, rows, currentEvent, additionalParams);
        }
    }
    
    /**
     * Fetches a specific page and caches it without updating signals
     */
    private prefetchPage(first: number, rows: number, baseEvent: LazyLoadEvent, additionalParams: any): void {
        // Create a copy of the current LazyLoadEvent with adjusted pagination
        const prefetchEvent: LazyLoadEvent = { 
            ...JSON.parse(JSON.stringify(baseEvent)),
            first: first,
            rows: rows
        };
        
        // Generate a key for this prefetch
        const prefetchParamsKey = this.generateParamsKey([prefetchEvent, additionalParams]);
        
        // Skip if we already have this page cached
        if (this.paramsCache.has(prefetchParamsKey)) {
            return;
        }
        
        // Convert to HttpParams
        let httpParams = new HttpParams();
        httpParams = httpParams.set('first', prefetchEvent.first.toString());
        httpParams = httpParams.set('rows', prefetchEvent.rows.toString());
        
        if (prefetchEvent.sortField && prefetchEvent.sortOrder) {
            httpParams = httpParams.set('sortField', prefetchEvent.sortField);
            httpParams = httpParams.set('sortOrder', prefetchEvent.sortOrder.toString());
        }
        
        if (prefetchEvent.globalFilter) {
            httpParams = httpParams.set('globalFilter', prefetchEvent.globalFilter.toString());
        }
        
        if (prefetchEvent.multiSortMeta) {
            httpParams = httpParams.set('multiSortMeta', JSON.stringify(prefetchEvent.multiSortMeta));
        }
        
        if (prefetchEvent.filters) {
            const filters = JSON.parse(JSON.stringify(prefetchEvent.filters));
            delete filters.global;
            Object.keys(filters).forEach(key => {
                if (!Array.isArray(filters[key])) {
                    filters[key] = [filters[key]];
                }
                if (Array.isArray(filters[key]) && filters[key].length == 1 && filters[key][0].value == null) {
                    delete filters[key];
                }
            });
            httpParams = httpParams.set('filters', JSON.stringify(filters));
        }
        
        if (typeof additionalParams === 'object' && additionalParams !== null) {
            Object.keys(additionalParams).forEach(key => {
                const value = additionalParams[key];
                    if (value !== undefined && value !== null) {
                        httpParams = httpParams.set(key, String(value));
                    }
                });
        }

        // Make the prefetch request silently (no signal updates)
        this.httpClient.get<ReponseFiltre<T>>(this.apiUrl, {
            params: httpParams,
            observe: 'response'
        }).pipe(
            tap(response => {
                if (response.body) {
                    // Only cache the result, don't update signals
                    this.paramsCache.set(prefetchParamsKey, response.body);
                }
            }),
            catchError(err => {
                // Silently fail for prefetch requests
                console.warn('Prefetch failed:', err);
                return throwError(() => err);
            })
        ).subscribe();
    }
}


declare module 'rxjs' {
    interface Observable<T> {
        toStore<T>(
            this: Observable<HttpEvent<T>>,
            config?: {
                hub?: HubWatcherConfig;
                httpClient?: HttpClient;
            }
        ): Store<T>;
        toArrayStore<T>(
            this: Observable<HttpEvent<ReponseFiltre<T>>>,
            config: {
                hub?: HubWatcherConfig;
                httpClient?: HttpClient;
                apiUrl?: string;
            }
        ): ArrayStore<T>;
        watchUpdates<T>(
            this: Observable<T>,
            config: {
                hub?: HubWatcherConfig & {
                    hubConnection: HubConnection;
                    idField: string;
                };
                caching?: {
                    key?: string;
                    cacheMap?: CacheMap<string, SignalWithWatcher<T>>;
                }
                transformFunction?: (data: T) => T
            }
        ): Observable<SignalWithWatcher<T>>;
        watchUpdatesAndEvents<T>(
            this: Observable<HttpEvent<T>>,
            config: {
                hub?: HubWatcherConfig & {
                    hubConnection: HubConnection;
                    idField: string;
                };
                caching?: {
                    key?: string;
                    cacheMap?: CacheMap<string, SignalWithWatcher<T>>;
                },
                transformFunction?: (data: T) => T
            }
        ): Observable<HttpEvent<SignalWithWatcher<T>>>;
    }
}

Observable.prototype.toStore = function <T>(
    this: Observable<HttpEvent<T>>
): Store<T> {
    return new Store<T>(this);
};

Observable.prototype.toArrayStore = function <T>(
    this: Observable<HttpEvent<ReponseFiltre<T>>>,
    config: {
        hub?: HubWatcherConfig;
        httpClient?: HttpClient;
        apiUrl?: string;
    }
): ArrayStore<T> {
    return new ArrayStore<T>(
        this,
        config.hub,
        config.httpClient,
        config.apiUrl
    );
};

Observable.prototype.watchUpdates = function <T>(
    this: Observable<T>,
    config: {
        hub: HubWatcherConfig & {
            hubConnection: HubConnection;
            idField: string;
        },
        caching?: {
            key?: string;
            cacheMap?: CacheMap<string, SignalWithWatcher<T>>;
        },
        transformFunction?: (data: T) => T
    }
): Observable<SignalWithWatcher<T>> {
    return new Observable<SignalWithWatcher<T>>(observer => {
        
        if (config.caching?.cacheMap && config.caching?.key && config.caching?.cacheMap?.has(config.caching.key)) {
            const signalWatcher = config.caching.cacheMap.get(config.caching.key);
            observer.next(signalWatcher);
            observer.complete();
            return;
        }

        this.subscribe({
            next: (value) => {
                let signalWatcher = new SignalWithWatcher<T>(
                    value,
                    config.hub.hubConnection,
                    config.hub.idField,
                    config.caching?.cacheMap,
                    config.caching?.key,
                    config.transformFunction,
                    config.hub.events
                );
                if (config.caching?.cacheMap && config.caching?.key) {
                    config.caching.cacheMap.set(config.caching.key, signalWatcher);
                }
                observer.next(signalWatcher);
            },
            error: (error) => {
                observer.error(error);
            },
            complete: () => observer.complete()
        });
    });
};

Observable.prototype.watchUpdatesAndEvents = function <T>(
    this: Observable<HttpEvent<T>>,
    config: {
        hub: HubWatcherConfig & {
            hubConnection: HubConnection;
            idField: string;
        },
            caching?: {
                key?: string;
                cacheMap?: CacheMap<string, SignalWithWatcher<T>>;
            },
            transformFunction?: (data: T) => T
        }
    ): Observable<HttpEvent<SignalWithWatcher<T>>> {
        return new Observable<HttpEvent<SignalWithWatcher<T>>>(observer => {
            
            if (config.caching?.cacheMap && config.caching?.key && config.caching?.cacheMap?.has(config.caching.key)) {
                const signalWatcher = config.caching.cacheMap.get(config.caching.key);
                observer.next({
                    type: HttpEventType.Response,
                    body: signalWatcher
                } as HttpResponse<SignalWithWatcher<T>>);
                observer.complete();
                return;
            }
    
            this.subscribe({
                next: (value) => {
                    if (value.type === HttpEventType.Response) {
                        let signalWatcher = new SignalWithWatcher<T>(
                            value.body,
                            config.hub.hubConnection,
                            config.hub.idField,
                            config.caching?.cacheMap,
                            config.caching?.key,
                            config.transformFunction,
                            config.hub.events
                        );
                        if (config.caching?.cacheMap && config.caching?.key) {
                            config.caching.cacheMap.set(config.caching.key, signalWatcher);
                        }
                        observer.next({
                            type: HttpEventType.Response,
                            body: signalWatcher
                        } as HttpResponse<SignalWithWatcher<T>>);
                    } else {
                        observer.next(value);
                    }
                },
                error: (error) => {
                    observer.error(error);
                },
                complete: () => observer.complete()
            });
        });
    };



export class SignalWithWatcher<T> {
    private _response: WritableSignal<T> = signal<T>(null);
    private _hub: HubConnection;
    private _idField: string;
    private _eventNames: { add: string; update: string; delete: string };
    private _cacheMap?: CacheMap<string, SignalWithWatcher<T>>;
    private _cacheKey?: string;
    private _reconnectAttempts = 0;
    public updates = signal<{ type: 'add' | 'update' | 'delete', data: T }>(null);
    public transformFunction: (data: T) => T;

    constructor(
        initialResponse: T,
        hub: HubConnection,
        idField: string,
        cacheMap?: CacheMap<string, SignalWithWatcher<T>>,
        cacheKey?: string,
        transformFunction?: (data: T) => T,
        events?: { add?: string; update?: string; delete?: string }
    ) {
        this._hub = hub;
        this._idField = idField;
        this._eventNames = {
            add: events?.add ?? 'Add',
            update: events?.update ?? 'Update',
            delete: events?.delete ?? 'Delete'
        };
        this._response.set(transformFunction ? transformFunction(initialResponse) : initialResponse);
        this._cacheMap = cacheMap;
        this._cacheKey = cacheKey;
        this.transformFunction = transformFunction;
        this.connect();
    }

    cache(
        key: string,
        cacheMap: CacheMap<string, SignalWithWatcher<T>>,
        config?: { ttl?: number }
    ): SignalWithWatcher<T> {
        if (!this._cacheMap) this._cacheMap = cacheMap;
        if (!this._cacheKey) this._cacheKey = key;

        if (!cacheMap.get(key)) {
            cacheMap.set(key, this);
        }

        return cacheMap.get(key);
    }

    get value(): WritableSignal<T> {
        return this._response;
    }

    private connect(): void {
        this.startConnection();
        this.registerOnServerEvents();
    }

    private startConnection(): void {
        const state = this._hub.state;
        if (state === HubConnectionState.Connected || state === HubConnectionState.Connecting || state === HubConnectionState.Reconnecting) {
            return;
        }

        this._hub
            .start()
            .then(() => {
                console.log('Connection to hub established');
                this._reconnectAttempts = 0;
            })
            .catch(err => {
                this._reconnectAttempts++;
                const delay = Math.min(1000 * Math.pow(2, this._reconnectAttempts - 1), 30000);
                console.error(`Error while establishing connection to hub (attempt ${this._reconnectAttempts}):`, err);
                setTimeout(() => this.startConnection(), delay);
            });
    }

    private registerOnServerEvents(): void {
        if (!Array.isArray(this._response())) {
            // Handle single object updates
            this._hub.on(this._eventNames.update, (data: T) => {
                // Only update if the ID matches
                if (this._response() && data && this._response()[this._idField] === data[this._idField]) {
                    this._response.set(this.transformFunction ? this.transformFunction(data) : data);
                    this.updates.set({ type: 'update', data: data });
                }
            });

            // Handle single object deletion
            this._hub.on(this._eventNames.delete, (data: T) => {
                if (this._response() && this._response()[this._idField] === data[this._idField]) {
                    const deletedData = this._response(); // Capture data before setting to null
                    this._response.set(null); // Set the signal to null
                    this.updates.set({ type: 'delete', data: deletedData }); // Notify about deletion

                    // Remove from cache if applicable
                    if (this._cacheMap && this._cacheKey) {
                        this._cacheMap.delete(this._cacheKey);
                        console.log(`Watcher with key ${this._cacheKey} deleted from cache.`);
                    }

                    this._hub.off(this._eventNames.delete);
                }
            });

        } else {
            // Handle array updates (existing logic)
            this._hub.on(this._eventNames.update, (data: T) => {
                // Let's cast response() to any[] for method calls
                const currentElements = this._response() as any[];
                // Assuming data is the element type TElement = T extends (infer U)[] ? U : T;
                // Need the element type for comparison. Let's assume data is TElement for now.
                 // We need idField on elements, not the array T.
                const elementData = data as any; // Cast data to any for property access, assuming it's the element

                if (currentElements.some(x => x[this._idField] === elementData[this._idField])) {
                    const updatedElements = currentElements.map(x =>
                        x[this._idField] === elementData[this._idField] ? elementData : x
                    );
                    // Set requires type T (the array type)
                    this._response.set(this.transformFunction ? this.transformFunction(updatedElements as T) : updatedElements as T);
                    this.updates.set({ type: 'update', data: elementData }); // Notify with element data

                    // Propagate updates to other cached watchers
                    if (this._cacheMap && this._cacheKey) {
                        this._cacheMap.forEach((watcher, key) => {
                            const watcherResponse = watcher._response();
                            // Ensure other watcher also holds an array
                            if (key !== this._cacheKey && Array.isArray(watcherResponse)) {
                                const otherElements = watcherResponse as any[]; // Cast for methods
                                if (otherElements.some(x => x[this._idField] === elementData[this._idField])) {
                                    const otherUpdatedElements = otherElements.map(x =>
                                        x[this._idField] === elementData[this._idField] ? elementData : x
                                    );
                                    // Set requires type T for that watcher
                                    watcher._response.set(this.transformFunction ? this.transformFunction(otherUpdatedElements as T) : otherUpdatedElements as T);
                                }
                            }
                        });
                    }
                }
            });

            this._hub.on(this._eventNames.add, (data: T) => { // Assuming data is TElement
                 const currentElements = this._response() as any[];
                 const elementData = data as any; // Cast data to any for property access

                 if (!currentElements.some(x => x[this._idField] === elementData[this._idField])) {
                    // Spread operator works on arrays
                    const newElements = [...currentElements, elementData];
                    this._response.set(this.transformFunction ? this.transformFunction(newElements as T) : newElements as T); // Set requires type T
                    this.updates.set({ type: 'add', data: elementData });

                    // Propagate updates to other cached watchers
                    if (this._cacheMap && this._cacheKey) {
                        this._cacheMap.forEach((watcher, key) => {
                            const watcherResponse = watcher._response();
                             if (key !== this._cacheKey && Array.isArray(watcherResponse)) {
                                const otherElements = watcherResponse as any[];
                                if (!otherElements.some(x => x[this._idField] === elementData[this._idField])) {
                                    const otherNewElements = [...otherElements, elementData];
                                    watcher._response.set(this.transformFunction ? this.transformFunction(otherNewElements as T) : otherNewElements as T);
                                }
                            }
                        });
                    }
                }
            });

            this._hub.on(this._eventNames.delete, (data: T) => {
                 const currentElements = this._response() as any[];
                 const itemToDelete = currentElements.find(x => x[this._idField] === data[this._idField]);

                if (itemToDelete) {
                    const filteredElements = currentElements.filter(x => x[this._idField] !== data[this._idField]);
                    this._response.set(this.transformFunction ? this.transformFunction(filteredElements as T) : filteredElements as T); // Set requires type T
                    // itemToDelete is an element, should match TElement type
                    this.updates.set({ type: 'delete', data: itemToDelete as any }); // Use itemToDelete

                    // Propagate updates to other cached watchers
                    if (this._cacheMap && this._cacheKey) {
                        this._cacheMap.forEach((watcher, key) => {
                             const watcherResponse = watcher._response();
                              if (key !== this._cacheKey && Array.isArray(watcherResponse)) {
                                const otherElements = watcherResponse as any[];
                                const otherItemToDelete = otherElements.find(x => x[this._idField] === data[this._idField]);
                                if (otherItemToDelete) {
                                    const otherFilteredElements = otherElements.filter(x => x[this._idField] !== data[this._idField]);
                                    watcher._response.set(this.transformFunction ? this.transformFunction(otherFilteredElements as T) : otherFilteredElements as T);
                                }
                            }
                        });
                    }
                }
            });
        }
    }

    public getResponse(): T[] {
        const response = this._response();
        // If the signature requires T[], we should return an array.
        // We assume T is the element type here, contradicting the class structure.
        // Let's stick to the structure: T is the data type (potentially array).
        // If T is User[], we return User[]. If T is User, we return ??? T[].
        // The signature T[] is likely wrong if T can be non-array.
        // Assuming the *intent* is to return elements if it holds an array:
        if (Array.isArray(response)) {
             return response as T[]; // If T=User[], this returns User[]. Correct.
        }
        // If T=User, response is User. Cannot return T[]. Return empty array.
        return [];
    }
}